/**
 * Opens the OneTrust privacy preference center programmatically.
 * 
 * This function triggers the existing OneTrust preference center modal,
 * which allows users to view and manage their cookie preferences.
 * 
 * The function handles cases where OneTrust may not be loaded yet by
 * polling for availability with a timeout.
 */
export function openOneTrustPreferenceCenter() {
  const MAX_WAIT_TIME = 5000 // 5 seconds
  const POLL_INTERVAL = 100 // Check every 100ms
  const startTime = Date.now()

  /**
   * Attempts to open the OneTrust preference center using available API methods
   * @returns {boolean} True if successfully opened, false otherwise
   */
  const tryOpenPreferenceCenter = () => {
    // Try newer OneTrust API first
    if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === "function") {
      try {
        window.OneTrust.ToggleInfoDisplay()
        return true
      } catch (error) {
        console.warn("Error calling OneTrust.ToggleInfoDisplay():", error)
        return false
      }
    }

    // Fallback to older Optanon API
    if (window.Optanon && typeof window.Optanon.ToggleInfoDisplay === "function") {
      try {
        window.Optanon.ToggleInfoDisplay()
        return true
      } catch (error) {
        console.warn("Error calling Optanon.ToggleInfoDisplay():", error)
        return false
      }
    }

    // Fallback: Try to programmatically click the preference center button
    const preferenceCenterButton = document.getElementById("onetrust-pc-btn-handler")
    if (preferenceCenterButton) {
      try {
        preferenceCenterButton.click()
        return true
      } catch (error) {
        console.warn("Error clicking OneTrust preference center button:", error)
        return false
      }
    }

    return false
  }

  // If OneTrust is already available, try to open immediately
  if (tryOpenPreferenceCenter()) {
    return
  }

  // If not available, poll for OneTrust to load in the background
  const pollInterval = setInterval(() => {
    const elapsed = Date.now() - startTime

    if (tryOpenPreferenceCenter()) {
      clearInterval(pollInterval)
      return
    }

    // Timeout after MAX_WAIT_TIME
    if (elapsed >= MAX_WAIT_TIME) {
      clearInterval(pollInterval)
      console.warn(
        "OneTrust preference center could not be opened. OneTrust may not be loaded yet."
      )
    }
  }, POLL_INTERVAL)
}

